import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import apiClient from "../api/apiClient";
import { DynamicLibraryModal } from "../components/PricingTable";
import { usePackageCheckout } from "../hooks/usePackageCheckout";
import { useLanguage } from "../i18n/LanguageContext";
import "./PackageDetail.css";

const PERIODS = {
  30: "/ Month",
  60: "/ 2 Months",
  90: "/ 3 Months",
  180: "/ 6 Months",
  365: "/ Year",
};

/**
 * A package's own page, at /packages/:slug.
 *
 * Everything below the price comes from the panel, so the page can be
 * rewritten and re-illustrated for a campaign without a deployment.
 */
const PackageDetail = () => {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const { startCheckout, loading: buying, error: buyError, dynamicPickerProps } = usePackageCheckout();

  const [pkg, setPkg] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    setPkg(undefined);

    apiClient.get(`/packages/${slug}`)
      .then(({ data }) => {
        if (!cancelled) setPkg(data.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setPkg(null);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, language]);

  if (pkg === undefined) {
    return (
      <div className="page-content hpa-package-page">
        <div className="container">
          <div className="hpa-package-state">
            <div className="spinner-border text-danger" role="status" />
            <p>{t("Loading package...")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (pkg === null) {
    return (
      <div className="page-content hpa-package-page">
        <div className="container">
          <div className="hpa-package-state">
            <h1>{t("This package is not available")}</h1>
            <p>{t("It may have been renamed or withdrawn. Browse everything on offer instead.")}</p>
            <Link to="/packages" className="hpa-package-btn">{t("See all packages")}</Link>
          </div>
        </div>
      </div>
    );
  }

  const period = PERIODS[pkg.duration_days]
    ? t(PERIODS[pkg.duration_days])
    : t("/ {days} Days", { days: pkg.duration_days });
  const discounted = Number(pkg.original_price) > Number(pkg.price);
  const benefits = pkg.benefits || [];
  const gallery = pkg.gallery_images || [];
  const points = pkg.description_points || [];

  const buyLabel = pkg.is_ultimate_program ? t("Request Your Program") : t("Buy This Package");

  return (
    <div className="page-content hpa-package-page">
      <header
        className={`hpa-package-hero${pkg.hero_image_url ? " has-image" : ""}`}
        style={pkg.hero_image_url ? { backgroundImage: `url(${pkg.hero_image_url})` } : undefined}
      >
        <div className="hpa-package-hero__shade" />
        <div className="container">
          <div className="hpa-package-hero__content">
            <nav aria-label="breadcrumb" className="hpa-package-crumbs">
              <Link to="/packages">{t("Packages")}</Link>
              <span aria-hidden="true">/</span>
              <span>{pkg.name}</span>
            </nav>

            {pkg.promotion_badge && <span className="hpa-package-badge">{pkg.promotion_badge}</span>}
            <h1>{pkg.page_headline || pkg.name}</h1>
            {pkg.page_headline && <p className="hpa-package-hero__name">{pkg.name}</p>}

            <div className="hpa-package-price">
              {discounted && <del>{pkg.currency} {pkg.original_price}</del>}
              <strong><small>{pkg.currency}</small> {pkg.price}</strong>
              <span>{period}</span>
            </div>

            {pkg.promotion_message && <p className="hpa-package-promo">{pkg.promotion_message}</p>}

            <div className="hpa-package-hero__actions">
              <button
                type="button"
                className="hpa-package-btn"
                onClick={() => startCheckout(pkg)}
                disabled={buying}
              >
                {buying ? t("Preparing payment…") : buyLabel}
              </button>
              <Link to="/packages" className="hpa-package-btn is-ghost">{t("Compare packages")}</Link>
            </div>

            {buyError && <p className="hpa-package-error" role="alert">{buyError}</p>}
          </div>
        </div>
      </header>

      <div className="container">
        {pkg.long_description && (
          <section className="hpa-package-block">
            <h2>{t("About this package")}</h2>
            {pkg.long_description.split(/\n{2,}/).map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </section>
        )}

        {benefits.length > 0 && (
          <section className="hpa-package-block">
            <h2>{t("What is included")}</h2>
            <div className="hpa-package-benefits">
              {benefits.map((benefit, index) => (
                <article key={index}>
                  <span aria-hidden="true"><i className="fa-solid fa-check" /></span>
                  <h3>{benefit.title}</h3>
                  {benefit.description && <p>{benefit.description}</p>}
                </article>
              ))}
            </div>
          </section>
        )}

        {points.length > 0 && (
          <section className="hpa-package-block">
            <h2>{t("At a glance")}</h2>
            <ul className="hpa-package-points">
              {points.map((point, index) => (
                <li key={index}><span aria-hidden="true">✓</span>{point}</li>
              ))}
            </ul>
          </section>
        )}

        {!pkg.is_ultimate_program && (pkg.libraries || []).length > 0 && (
          <section className="hpa-package-block">
            <h2>{t("Libraries you unlock")}</h2>
            {pkg.is_dynamic && (
              <p className="hpa-package-note">
                {t("Choose {count} libraries", { count: pkg.library_selection_count })}
              </p>
            )}
            <div className="hpa-package-libraries">
              {pkg.libraries.map((library) => <span key={library.id}>{library.name}</span>)}
            </div>
          </section>
        )}

        {pkg.is_ultimate_program && (
          <section className="hpa-package-block">
            <h2>{t("Libraries you unlock")}</h2>
            <p>{t("Every current and future library")}</p>
          </section>
        )}

        {gallery.length > 0 && (
          <section className="hpa-package-block">
            <h2>{t("Inside the package")}</h2>
            <div className="hpa-package-gallery">
              {gallery.map((image, index) => (
                <figure key={index}>
                  <img src={image} alt="" loading="lazy" />
                </figure>
              ))}
            </div>
          </section>
        )}

        <section className="hpa-package-close">
          <h2>{t("Ready to start?")}</h2>
          <p>{t("Your access opens as soon as the payment clears.")}</p>
          <button
            type="button"
            className="hpa-package-btn"
            onClick={() => startCheckout(pkg)}
            disabled={buying}
          >
            {buying ? t("Preparing payment…") : buyLabel}
          </button>
        </section>
      </div>

      <DynamicLibraryModal {...dynamicPickerProps} />
    </div>
  );
};

export default PackageDetail;
